import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Gauge, Smartphone, Search } from 'lucide-react';
import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath } from '@/lib/locale';
import Seo from '@/components/Seo';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import { persistUtmFromSearch, readUtmFromSession } from '@/lib/utm';
import { insertChecklistLead } from '@/lib/supabase';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormState = {
  name: string;
  email: string;
  honeypot: string;
};

type FieldErrors = {
  name?: string;
  email?: string;
  submit?: string;
};

export default function ChecklistPage() {
  const { language, t, tList } = useLanguage();
  const localizedPath = useLocalizedPath();
  const location = useLocation();
  const navigate = useNavigate();
  const isGreek = language === 'el';
  const formStartRef = useRef(Date.now());

  const includedItems = [
    { icon: Gauge, title: t('checklist.included.items.0.title'), description: t('checklist.included.items.0.desc') },
    { icon: Smartphone, title: t('checklist.included.items.1.title'), description: t('checklist.included.items.1.desc') },
    { icon: Search, title: t('checklist.included.items.2.title'), description: t('checklist.included.items.2.desc') },
  ];

  const steps = [
    { title: t('checklist.steps.items.0.title'), description: t('checklist.steps.items.0.desc') },
    { title: t('checklist.steps.items.1.title'), description: t('checklist.steps.items.1.desc') },
    { title: t('checklist.steps.items.2.title'), description: t('checklist.steps.items.2.desc') },
  ];

  const [form, setForm] = useState<FormState>({ name: '', email: '', honeypot: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    formStartRef.current = Date.now();
  }, []);

  useEffect(() => {
    persistUtmFromSearch(location.search);
  }, [location.search]);

  const seoMeta = useMemo(() => {
    if (isGreek) {
      return {
        title: 'Δωρεάν Checklist Επαγγελματικής Ιστοσελίδας | PixelPro Solutions',
        description:
          'Έλεγξε σε 10 λεπτά αν η ιστοσελίδα σου χάνει πελάτες. Κατέβασε το δωρεάν PDF checklist και δες τι λείπει.',
        canonical: `${BASE_URL}/checklist`,
      };
    }
    return {
      title: 'Free Professional Website Checklist | PixelPro Solutions',
      description:
        'Check in 10 minutes if your website is losing customers. Download the free PDF checklist and see what’s missing.',
      canonical: `${BASE_URL}/en/checklist`,
    };
  }, [isGreek]);

  const validate = (): FieldErrors => {
    const nextErrors: FieldErrors = {};
    if (!form.name.trim()) nextErrors.name = t('checklist.form.nameError');
    if (!form.email.trim()) {
      nextErrors.email = t('checklist.form.emailError');
    } else if (!emailRegex.test(form.email.trim())) {
      nextErrors.email = t('checklist.form.emailInvalid');
    }
    return nextErrors;
  };

  const trackLead = (payload: Record<string, unknown>) => {
    const gtag = (globalThis as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof window !== 'undefined' && gtag) {
      gtag('event', 'generate_lead', payload);
    }

    const fbq = (globalThis as { fbq?: (...args: unknown[]) => void }).fbq;
    if (typeof window !== 'undefined' && fbq) {
      fbq('track', 'Lead', payload);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const utm = readUtmFromSession();
    const timeToSubmitMs = Date.now() - formStartRef.current;

    try {
      const summary = JSON.stringify({
        utm,
        path: location.pathname,
        timestamp: Date.now(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        timeToSubmitMs,
      });

      await insertChecklistLead({
        name: form.name.trim(),
        email: form.email.trim(),
        language,
        source: 'checklist',
        summary,
      });

      trackLead({
        lead_source: 'checklist',
        locale: language,
        page_path: location.pathname,
      });

      navigate(localizedPath('/thank-you/checklist'));
    } catch {
      setErrors({ submit: t('checklist.form.submitError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title={seoMeta.title}
        description={seoMeta.description}
        url={seoMeta.canonical}
        hreflang={getHreflangLinks('/checklist', language)}
      />

      <div className="relative min-h-screen bg-[#080c0d] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" aria-hidden="true" />
            <Container className="relative grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
              <div className="space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-brand-highlight/70">
                    {t('checklist.eyebrow')}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {t('checklist.title')}
                  </Display>
                </div>
                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {t('checklist.subtitle')}
                </Display>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
                  {t('checklist.bulletsLead')}
                </p>
                <ul className="space-y-3 text-white/80 text-base md:text-lg">
                  {tList('checklist.bullets').map((bullet, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-brand-highlight/80" aria-hidden="true" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative space-y-6">
                <div className="pointer-events-none absolute -top-12 -right-10 h-32 w-32 rounded-full bg-brand-highlight/20 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -bottom-16 -left-12 h-36 w-36 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />

                <div className="rounded-[32px] border border-white/15 bg-black/70 backdrop-blur-xl p-6 md:p-8 shadow-glass">
                  <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3">
                    {t('checklist.form.title')}
                  </h2>
                  <p className="text-white/70 mb-6">
                    {t('checklist.form.subtitle')}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="checklist-name" className="block text-sm text-white/70 mb-2">
                        {t('checklist.form.nameLabel')}
                      </label>
                      <input
                        id="checklist-name"
                        type="text"
                        value={form.name}
                        onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                        className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                        placeholder={t('checklist.form.namePlaceholder')}
                        required
                      />
                      {errors.name && <p className="mt-2 text-sm text-brand-highlight">{errors.name}</p>}
                    </div>

                    <div>
                      <label htmlFor="checklist-email" className="block text-sm text-white/70 mb-2">
                        {t('checklist.form.emailLabel')}
                      </label>
                      <input
                        id="checklist-email"
                        type="email"
                        value={form.email}
                        onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                        className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                        placeholder={t('checklist.form.emailPlaceholder')}
                        required
                      />
                      {errors.email && <p className="mt-2 text-sm text-brand-highlight">{errors.email}</p>}
                    </div>

                    <div className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
                      <label htmlFor="checklist-company">Company</label>
                      <input
                        id="checklist-company"
                        type="text"
                        value={form.honeypot}
                        onChange={(event) => setForm((prev) => ({ ...prev, honeypot: event.target.value }))}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {errors.submit && <p className="text-sm text-brand-highlight">{errors.submit}</p>}

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full justify-center"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t('checklist.form.loading') : t('checklist.form.submit')}
                    </Button>

                  <p className="text-xs text-white/50">
                    {t('checklist.form.privacy')}
                  </p>
                </form>
              </div>

            </div>
          </Container>
        </HeroSection>

          <Section className="relative overflow-hidden">
            <div className="absolute inset-0 geometric-pattern opacity-30" aria-hidden="true" />
            <Container className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80 mb-3">
                      {t('checklist.included.title')}
                    </p>
                    {t('checklist.included.subtitle') && (
                      <h2 className="text-3xl font-semibold text-white md:text-4xl">
                        {t('checklist.included.subtitle')}
                      </h2>
                    )}
                  </div>
                </div>

              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {includedItems.map((item) => (
                  <div
                    key={item.title}
                    className="group rounded-3xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors duration-300 group-hover:border-brand-highlight/70 group-hover:text-brand-highlight">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-white">{item.title}</h3>
                    <p className="mt-3 text-sm text-white/70">{item.description}</p>
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/90 via-black/80 to-black" aria-hidden="true" />
            <Container className="relative">
              <div className="rounded-[32px] border border-white/15 bg-black/70 p-8 md:p-12 shadow-glass">
                <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80 mb-3">
                      {t('checklist.steps.title')}
                    </p>
                    <h2 className="text-3xl font-semibold text-white md:text-4xl">
                      {t('checklist.steps.subtitle')}
                    </h2>
                  </div>
                </div>

                <div className="mt-10 grid gap-6 md:grid-cols-3">
                  {steps.map((step, index) => (
                    <div key={step.title} className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 shadow-glass">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-highlight/60 bg-brand-highlight/10 text-sm font-semibold text-brand-highlight">
                          {index + 1}
                        </span>
                        <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                      </div>
                      <p className="mt-4 text-sm text-white/70">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
